/* eslint-disable react/prop-types */
import { message } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import React, { useEffect, useMemo } from "react"
import { useHistory } from "react-router"
import styled from "styled-components"

import { USER_AUTH_HEADER, updateBookingBio } from "../../../../../api"
import BookingMetaForm from "../../../../_shared/components/BookingForm/BookingMetaForm"
import StyledScreenTitle from "../../../../_shared/components/Typography/ScreenTitle"
import { useStateValue } from "../../../../_shared/context/AppStateStore"
import useForm from "../../../../_shared/hooks/useForm"
import useRestTable from "../../../../_shared/hooks/usePaginatedRestResource"

// ----------------------------------------------------------------------------

function _EditBooking(props) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className, match } = props

    // -------------------------------------
    // Hooks (e.g. useState, ...)
    // -------------------------------------

    const [{ user }] = useStateValue()
    const history = useHistory()
    const [bookings] = useRestTable(
        `/bookings/user-bookings/${user.id}`,
        USER_AUTH_HEADER
    )

    const bookingId = match.params.booking

    const [form, setOne, setMany] = useForm()

    // -------------------------------------
    // Memoized values
    // -------------------------------------

    const booking = useMemo(() => {
        return bookings?.find((b) => b.id === bookingId)
    }, [bookingId, bookings])

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        booking && setMany(booking)
        // eslint-disable-next-line
    }, [booking])

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function validateEmail(email) {
        const re =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return re.test(String(email).toLowerCase())
    }

    function validateDateFormat(date) {
        const format = "YYYY-MM-DD"
        const pat = /[0-9]{4}-[0-9]{2}-[0-9]{2}/

        if (!pat.test(date) || !(dayjs(date, format).format(format) === date)) {
            throw new Error(
                "La data di nascita deve essere nel formato YYYY-MM-DD"
            )
        }
    }

    async function handleSubmit(e) {
        e.preventDefault()
        try {
            if (!validateEmail(form?.email)) {
                return message.error("Indirizzo email non valido.")
            }

            validateDateFormat(form?.birth)
            const newBookingBody = {
                birth: form.birth,
                name: form.name,
                lastName: form.lastName,
                phone: form.phone,
                email: form.email,
                residence: form.residence,
            }

            await updateBookingBio(newBookingBody, booking.id, {
                userId: user.id,
            })
            history.push("/booking/edit")
        } catch (e) {
            message.error(e.message)
        }
    }

    function renderBookingMeta() {
        return (
            booking && (
                <div className="booking-meta">
                    <p>
                        Modifica la tua prenotazione per l&apos;evento{" "}
                        <b
                            dangerouslySetInnerHTML={{
                                __html: booking.event.title,
                            }}
                        ></b>{" "}
                        in data{" "}
                        <b>{dayjs(booking.slot.date).format("DD/MM/YYYY")}</b>{" "}
                        nella fascia oraria <b>{booking.slot.window}</b>
                    </p>
                </div>
            )
        )
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    return (
        <div className={`${className}`}>
            <StyledScreenTitle
                className="screen-title"
                text="Modifica dettagli prenotazione"
            ></StyledScreenTitle>
            <form onSubmit={handleSubmit}>
                {renderBookingMeta()}
                {form?.id && (
                    <BookingMetaForm values={form} onChange={setOne} />
                )}
            </form>
        </div>
    )
}

// ----------------------------------------------------------------------------
// Component PropTypes and default props
// ----------------------------------------------------------------------------

_EditBooking.propTypes = {
    className: PropTypes.string.isRequired,
}

_EditBooking.defaultProps = {}

// ----------------------------------------------------------------------------

const EditBooking = styled(_EditBooking)`
    & {
        margin-top: 85px;
        display: flex;
        flex-direction: column;
        align-items: "center";
        max-width: 1980px;
        padding: 0px 110px;
        padding-bottom: 50px;

        .screen-title {
            margin-bottom: 40px;
        }

        .booking-meta {
            margin-bottom: 20px;
            font-size: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            p {
                width: 100%;
                text-align: center;
                max-width: 400px;
                p {
                    margin: 0;
                    display: inline;
                }
            }
        }

        @media (max-width: 980px) {
            padding: 0px 70px;
        }
        @media (max-width: 768px) {
            padding: 0px 40px;
            .screen-title {
                font-size: 26px;
            }
        }
        @media (max-width: 576px) {
            padding: 0px 10px;
        }
    }
`
// ----------------------------------------------------------------------------

export default EditBooking
